import { useContext, useEffect } from "react";
import "./Orders.scss";
import { Button } from "../../../Components/Button";
import useApi from "../../../hooks/useApi";

import { Title } from "../../../Components/Typography";
import { LogoSection } from "../../../Components/LogoSection";
import useResponsive from "../../../hooks/useResponsive";
import { Table } from "../../../Components/Table";
import useDatabase from "../../../hooks/useDatabase";
import Filters from "./Filters/Filters";

import {
  AdminContext,
  AdminContextProps,
} from "../../../Contexts/AdminContext";
import { DateFilterEnum } from "../../../types/DateFilterEnum";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../Router";

import { useLogin } from "../../../Contexts/LoginAdminContext";

export const Orders = () => {
  const { isDesktopOrLaptop } = useResponsive();
  const { getCollection, loading } = useDatabase();
  const navigate = useNavigate();
  const { getReport, loading: reportLoading } = useApi();

  const handleDownloadReport = async () => {
    try {
      let fechaInicio = '';
      let fechaFin = moment().format('YYYY-MM-DD'); // Fecha actual
  
      // Determinar el rango de fechas según la selección
      switch (orderFilter.checkedDate) {
        case DateFilterEnum.HOY:
          fechaInicio = moment().format('YYYY-MM-DD');
          break;
        case DateFilterEnum.AYER:
          fechaInicio = moment().subtract(1, 'days').format('YYYY-MM-DD');
          break;
        case DateFilterEnum.SEMANA:
          fechaInicio = moment().subtract(7, 'days').format('YYYY-MM-DD');
          break;
        case DateFilterEnum.TODOS:
          // Para "Todos" no establecemos fecha de inicio
          fechaInicio = '';
          break;
        default:
          fechaInicio = moment().format('YYYY-MM-DD');
      }
  
      // Construir el parámetro para el backend
      const fechaParam = fechaInicio ? `${fechaInicio}` : 'all';
  
      const response = await getReport(fechaParam);
      
      if (response && response.success) {
        downloadCSV(response.datos, `reporte_${fechaParam === 'all' ? 'completo' : fechaParam}.csv`);
      } else {
        alert('No se pudo generar el reporte');
      }
    } catch (error) {
      console.error("Error al descargar el reporte:", error);
      alert('Error al generar el reporte');
    }
  };


  interface Order {
    nombre_cliente: string;
    total: number;
    estado_pago: string;
    codigo_seguimiento: string;
    fecha_creacion: string;
  }
  
  const downloadCSV = (data: Order[], filename: string) => {
    try {
      // Verificar si la data es un array con al menos un elemento
      if (!Array.isArray(data) || data.length === 0) {
        alert('No hay datos para exportar');
        return;
      }
  
      // El primer elemento del array contiene los datos de los pedidos
      const ordersData = data[0];
      
      // Extraer los valores del objeto de pedidos
      const reportData = Object.values(ordersData);
  
      // Verificar si hay datos
      if (reportData.length === 0) {
        alert('No hay datos para exportar');
        return;
      }
  
      // Definir las columnas del CSV
      const headers = ['Nombre Cliente', 'Total', 'Estado Pago', 'Codigo Pedido', 'Fecha'];
      
      // Crear las filas del CSV
      const rows = reportData.map(item => [
        item.nombre_cliente,
        item.total,
        item.estado_pago,
        item.codigo_seguimiento,
        moment(item.fecha_creacion).format('YYYY-MM-DD'), // Formatear la fecha
      ]);
  
      // Crear el contenido CSV
      const csvContent = [
        headers.join(';'), // Usamos punto y coma como delimitador
        ...rows.map(row => row.join(';'))
      ].join('\n');
  
      // Crear y descargar el archivo
      const bom = '\uFEFF';
      const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error al generar CSV:', error);
      alert('Error al generar el archivo CSV');
    }
  };

  const {
    orderFilter,
    setOrderFilter,
    orders,
    formatOrders,
    setSelectedOrder,
    selectedRowIndex,
  }: AdminContextProps = useContext<any>(AdminContext);

  const { email } = useLogin();

  useEffect(() => {
    getCollection("view_order").then(formatOrders);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickRow = (order: any, i: number) => {
    setSelectedOrder(order);
    navigate(routes.adminOrdersDetails.path);
  };

  const getFilteredOrders = () => {
    let filteredOrders = orders.filter(
      (order: any) => orderFilter.checkedStatus.indexOf(order.id_status) !== -1
    );
  
    // Filtrado por fecha
    if (orderFilter.checkedDate !== DateFilterEnum.TODOS) {
      filteredOrders = filteredOrders.filter((order: any) => {
        const orderDate = moment(order.create_time);
        
        switch (orderFilter.checkedDate) {
          case DateFilterEnum.HOY:
            return orderDate.isSame(moment(), 'day');
          case DateFilterEnum.AYER:
            return orderDate.isSame(moment().subtract(1, 'days'), 'day');
          case DateFilterEnum.SEMANA:
            return orderDate.isAfter(moment().subtract(7, 'days'));
          default:
            return true;
        }
      });
    }
  
    // Manejar valores nulos en name
    if (orderFilter.name) {
      filteredOrders = filteredOrders.filter(
        (order: any) => 
          order.name &&  // Verificar que name no sea null/undefined
          order.name.toLowerCase().indexOf(orderFilter.name.toLowerCase()) !== -1
      );
    }
  
    // Manejar valores nulos en tracking_code
    if (orderFilter.tracking_code) {
      filteredOrders = filteredOrders.filter(
        (order: any) => 
          order.tracking_code &&  // Verificar que tracking_code no sea null/undefined
          order.tracking_code.indexOf(orderFilter.tracking_code) !== -1
      );
    }
  
    return filteredOrders;
  };

  return (
    <div
      className={
        isDesktopOrLaptop ? "admin-orders-desktop" : "admin-orders-mobile"
      }
    >
      <LogoSection showInstructions={false} />

      <div className="main-column">
        <div className="top-content">
          <div className="title-section">
            <Title>Órdenes</Title>
              <div className="title-actions">
                <Button 
                  onClick={() => navigate(routes.adminUpdatePrices.path)}
                  outlined
                  small
                  style={{ marginRight: '8px' }}
                >
                  Actualizar Copias
                </Button>
                <Button 
                  onClick={() => navigate(routes.adminUpdateAnillado.path)}
                  outlined
                  small
                >
                  Actualizar Anillado
                </Button>
              </div>
            <p>Usuario logueado: {email}</p>
          </div>

          <Filters orderFilter={orderFilter} setOrderFilter={setOrderFilter} onDownloadReport={handleDownloadReport} reportLoading={reportLoading} />

          <div className="table-section">
            <Table
              loading={loading}
              columns={
                isDesktopOrLaptop
                  ? [
                      { key: "tracking_code", text: "Nro Order" },
                      { key: "name", text: "Name" },
                      { key: "create_date", text: "Fecha" },
                      { key: "qty_files", text: "Archivos" },
                      { key: "qty_pages", text: "Páginas" },
                      { key: "addressType", text: "Entrega" },
                      { key: "id_status", text: "Estado", align: "center" },
                      { key: "payment_status", text: "Estado del pago", align: "center" },
                    ]
                  : [
                      { key: "tracking_code", text: "Pedido" },
                      { key: "create_date", text: "Fecha" },
                      { key: "id_status", text: "Estado", align: "center" },
                      { key: "payment_status", text: "Estado del pago", align: "center" },
                    ]
              }
              rows={getFilteredOrders()}
              onClickRow={onClickRow}
              selectedRowIndex={selectedRowIndex}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
